import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import CloseIcon from "@mui/icons-material/Close";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import PriorityHighOutlinedIcon from "@mui/icons-material/PriorityHighOutlined";

const FLOW_STEPS = [
  {
    title: "Rellena los bloques",
    text: "Abre cada sección a la derecha. Escribe como si le explicaras el cliente a un diseñador nuevo el primer día. Frases cortas valen; lo importante es ser concreto.",
  },
  {
    title: "Prioriza lo creativo",
    text: "Cliente, temporada y refs del CRM ya suelen venir puestos. Tu trabajo es la identidad, el look, tipologías, buenos/malos y la «frase de oro».",
  },
  {
    title: "Guarda a menudo",
    text: "Pulsa «Guardar borrador» cada poco. Si cierras el navegador sin guardar, se pierde lo último escrito.",
  },
  {
    title: "Cuando acabes: Terminada por Diseño",
    text: "Solo cuando creas que otro diseñador STAGE podría diseñar bien sin preguntarte. Miguel revisa y marca lista para crear el skill.",
  },
  {
    title: "Una persona a la vez",
    text: "Si ves el aviso de que alguien está editando, espera. Dos personas guardando a la vez machacan el trabajo de la otra.",
  },
];

const WHY_BLOCKS = [
  {
    title: "Qué es esto (sin tecnicismos)",
    body: [
      "Es el briefing del cliente convertido en «manual de instrucciones» para un agente de diseño.",
      "Ese agente no conoce Lola, ni Zara, ni vuestra historia: solo sabe lo que escribáis aquí.",
      "Cuanto más específico sea el briefing, menos inventará cosas genéricas y más parecerá STAGE + este cliente.",
    ],
  },
  {
    title: "Qué nos diferencia (esto es lo crítico)",
    body: [
      "No basta con «femenino», «premium» o «moderno»: eso lo escribe todo el mundo.",
      "Hay que escribir lo que SOLO este cliente hace: prints firmados, colocación del logo, siluetas que repiten, materiales que odian, errores que ya cometimos.",
      "Si quitas el nombre del cliente y el texto podría valer para otra marca, aún no está bien.",
      "Los agentes fallan cuando el briefing es vago. Acertan con ejemplos buenos, malos y una regla de oro.",
    ],
  },
];

const SECTION_GUIDE = [
  {
    name: "Identidad de marca",
    must: [
      "Una frase que defina la marca en chaquetas/exterior (actitud + look).",
      "Mujer target real: edad mental, ocasión, qué quiere SENTIR.",
      "Lista SÍ / NO de palabras (mantras y prohibidos).",
      "Si hay dos líneas: en qué se diferencian.",
    ],
    avoid: [
      "«Mujer moderna y elegante» sin más detalle.",
      "Copiar el claim de la web sin aterrizarlo a chaquetas.",
    ],
  },
  {
    name: "Universo visual",
    must: [
      "Colores core + temporada + vetados.",
      "Prints: cuáles, intensidad y DÓNDE van en la chaqueta.",
      "Materiales SÍ y NO (tacto, peso, brillo).",
      "Hardware y detalles firma reconocibles a 3 metros.",
    ],
    avoid: [
      "Solo «colores vivos» sin ejemplos.",
      "Dejar materiales en blanco.",
    ],
  },
  {
    name: "Producto / tipologías",
    must: [
      "Lista clara con rol (hero / core / commercial).",
      "Por tipología: fit, largo, cierre, must-have, errores.",
      "Precio o rango percibido si lo sabéis.",
      "Checklist «esto es la marca»: 4–8 puntos.",
    ],
    avoid: [
      "Mezclar tipologías sin estructura.",
      "Olvidar el fit.",
    ],
  },
  {
    name: "Memoria STAGE (refs)",
    must: [
      "5–8 refs con POR QUÉ cada una.",
      "Learnings: qué pide / rechaza / vende.",
      "Producción: MOQ, problemas, tallaje.",
    ],
    avoid: [
      "Pegar 50 códigos sin explicar.",
      "Ignorar refs CRM precargadas.",
    ],
  },
  {
    name: "Buenos / malos + frase de oro",
    must: [
      "Mínimo 3 buenos con por qué.",
      "Mínimo 3 malos con error concreto.",
      "Una frase de oro imposible de olvidar.",
    ],
    avoid: [
      "Solo fotos sin comentario.",
      "Malos genéricos («feo»).",
    ],
  },
];

const AGENT_FAILS = [
  "Briefing genérico → diseños sin ADN del cliente.",
  "Sin ejemplos malos → se cuela el minimalismo o el outdoor.",
  "Sin fit/largo/cierre → siluetas incorrectas.",
  "Sin constraints de producción → diseños inviables.",
  "Sin refs internas → ignora lo ya ganado con el cliente.",
  "Sin frase de oro → no hay criterio claro de rechazo.",
];

const BEFORE_DONE = [
  "¿Si quito el nombre del cliente, el texto sigue siendo reconocible?",
  "¿Hay al menos 3 buenos y 3 malos con explicación?",
  "¿Hay frase de oro?",
  "¿Están tipologías + fit + materiales SÍ/NO?",
  "¿Hay 5–8 refs con «por qué»?",
  "¿Guardaste el borrador justo antes?",
];

function GuideBlock({ title, children, defaultExpanded = false }) {
  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      elevation={0}
      sx={{
        bgcolor: "transparent",
        "&:before": { display: "none" },
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0, minHeight: 40 }}>
        <Typography fontWeight={700} variant="body2">
          {title}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 0, pt: 0, pb: 1.25 }}>{children}</AccordionDetails>
    </Accordion>
  );
}

function BulletList({ items, icon: Icon, color }) {
  return (
    <Stack spacing={0.65}>
      {items.map((item) => (
        <Stack key={item} direction="row" spacing={1} alignItems="flex-start">
          {Icon ? (
            <Icon fontSize="small" sx={{ color, mt: 0.15, flexShrink: 0 }} />
          ) : (
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                bgcolor: color || "primary.main",
                mt: 0.85,
                flexShrink: 0,
              }}
            />
          )}
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8125rem" }}>
            {item}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

/**
 * @param {{ open: boolean, onClose: () => void, isMiguel?: boolean, variant?: 'sidebar' | 'stack' }} props
 */
export default function SkillFichaHelpPanel({
  open,
  onClose,
  isMiguel,
  variant = "sidebar",
}) {
  const theme = useTheme();
  const isSidebar = variant === "sidebar";

  if (!open) return null;

  const content = (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 2,
          bgcolor: alpha(theme.palette.background.paper, 0.92),
          backdropFilter: "blur(8px)",
          pb: 1,
          mb: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: "primary.main",
            }}
          >
            <HelpOutlineOutlinedIcon fontSize="small" />
          </Box>
          <Box>
            <Typography fontWeight={800} variant="subtitle1">
              ¿Cómo funciona esto?
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Guía · lo que el agente necesita
            </Typography>
          </Box>
        </Stack>
        <IconButton size="small" onClick={onClose} aria-label="Cerrar ayuda">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Typography variant="body2" sx={{ mb: 1.5, fontSize: "0.8125rem" }}>
        Esto <b>no es un formulario cualquiera</b>: es el briefing con el que
        entrenaremos un agente para que diseñe <b>como STAGE para este cliente</b>.
        Si el briefing es flojo, el diseño será flojo.
      </Typography>

      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        <Chip size="small" label="Sé concreto" color="primary" variant="outlined" />
        <Chip size="small" label="Buenos y malos" variant="outlined" />
        <Chip size="small" label="Frase de oro" variant="outlined" />
        <Chip size="small" label="Refs + por qué" variant="outlined" />
      </Stack>

      <GuideBlock title="1. Pasos del día a día" defaultExpanded>
        <Stack spacing={1.1}>
          {FLOW_STEPS.map((step, i) => (
            <Stack key={step.title} direction="row" spacing={1.1} alignItems="flex-start">
              <Box
                sx={{
                  minWidth: 22,
                  height: 22,
                  borderRadius: "50%",
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                  fontSize: 11,
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mt: 0.15,
                }}
              >
                {i + 1}
              </Box>
              <Box>
                <Typography variant="body2" fontWeight={700} sx={{ fontSize: "0.8125rem" }}>
                  {step.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.78rem" }}>
                  {step.text}
                </Typography>
              </Box>
            </Stack>
          ))}
        </Stack>
      </GuideBlock>

      {WHY_BLOCKS.map((block) => (
        <GuideBlock key={block.title} title={block.title} defaultExpanded={false}>
          <BulletList items={block.body} />
        </GuideBlock>
      ))}

      <GuideBlock title="2. Qué poner en cada bloque" defaultExpanded>
        <Stack spacing={1.25}>
          {SECTION_GUIDE.map((sec) => (
            <Box
              key={sec.name}
              sx={{
                p: 1.25,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.background.default, 0.65),
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography fontWeight={700} variant="body2" sx={{ mb: 0.75 }}>
                {sec.name}
              </Typography>
              <Typography
                variant="caption"
                fontWeight={700}
                color="success.main"
                sx={{ display: "block", mb: 0.35 }}
              >
                SÍ poner
              </Typography>
              <BulletList
                items={sec.must}
                icon={CheckCircleOutlineIcon}
                color={theme.palette.success.main}
              />
              <Typography
                variant="caption"
                fontWeight={700}
                color="error.main"
                sx={{ display: "block", mt: 0.75, mb: 0.35 }}
              >
                Evitar
              </Typography>
              <BulletList
                items={sec.avoid}
                icon={CancelOutlinedIcon}
                color={theme.palette.error.main}
              />
            </Box>
          ))}
        </Stack>
      </GuideBlock>

      <GuideBlock title="3. Por qué falla el agente">
        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1 }}>
          <PriorityHighOutlinedIcon color="warning" fontSize="small" sx={{ mt: 0.15 }} />
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.78rem" }}>
            Fallos típicos cuando la ficha está a medias:
          </Typography>
        </Stack>
        <BulletList items={AGENT_FAILS} color={theme.palette.warning.main} />
      </GuideBlock>

      <GuideBlock title="4. Antes de «Terminada por Diseño»" defaultExpanded>
        <BulletList
          items={BEFORE_DONE}
          icon={CheckCircleOutlineIcon}
          color={theme.palette.primary.main}
        />
      </GuideBlock>

      <GuideBlock title="Ejemplo rápido de calidad">
        <Stack spacing={0.75}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.78rem" }}>
            <b style={{ color: theme.palette.success.main }}>Bien:</b> «Bomber
            ligera, raglan, zip vista, animal print; fit regular-cropped; se
            reconoce a 3 m; no outdoor neutro.»
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.78rem" }}>
            <b style={{ color: theme.palette.error.main }}>Mal:</b> «Chaqueta
            moderna y femenina, colores de moda.»
          </Typography>
        </Stack>
      </GuideBlock>

      {isMiguel && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="flex-start"
          sx={{
            mt: 1.25,
            pt: 1.25,
            borderTop: "1px dashed",
            borderColor: "divider",
          }}
        >
          <LightbulbOutlinedIcon fontSize="small" color="warning" sx={{ mt: 0.15 }} />
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.78rem" }}>
            <b>Miguel:</b> revisa identidad, buenos/malos y frase de oro. Solo tú
            «Marcas lista». Si falta detalle, «Reabrir para Diseño».
          </Typography>
        </Stack>
      )}
    </>
  );

  if (isSidebar) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          borderRadius: { xs: 0, lg: 3 },
          border: "1px solid",
          borderColor: alpha(theme.palette.primary.main, 0.22),
          bgcolor: alpha(theme.palette.primary.main, 0.04),
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            p: { xs: 1.5, sm: 2 },
            "&::-webkit-scrollbar": { width: 8 },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: alpha(theme.palette.primary.main, 0.25),
              borderRadius: 4,
            },
          }}
        >
          {content}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        mb: 2,
        p: 2,
        borderRadius: 3,
        border: "1px solid",
        borderColor: alpha(theme.palette.primary.main, 0.28),
        bgcolor: alpha(theme.palette.primary.main, 0.05),
        maxHeight: "55vh",
        overflow: "auto",
      }}
    >
      {content}
    </Box>
  );
}
